import type { LabelOutputV1, NumericDescriptionOutputV1 } from '@/sdk';
import type { Maybe } from '@/utilities.types';

export enum UserDefinedContextType {
  Toggle = 'Toggle',
  Label = 'Label',
  Numeric = 'Numeric',
}

export const isUserDefinedContextType = (type: string): type is UserDefinedContextType => {
  return Object.values(UserDefinedContextType).includes(type as UserDefinedContextType);
};

export type UserDefinedContextConfigErrors = Record<string, Maybe<string>>;

// Base interface with common fields
export interface UserDefinedContextConfigBase {
  key: string;
  type: UserDefinedContextType;
  name: string;
  icon: string;
  errors?: UserDefinedContextConfigErrors;
}

// Interface for Boolean and Label context types
export interface UserDefinedContextLabelsConfig extends UserDefinedContextConfigBase {
  type: UserDefinedContextType.Toggle | UserDefinedContextType.Label;
  categoryId: string;
  labels: LabelOutputV1[];
}

// Interface for Numeric context type
export interface UserDefinedContextNumericConfig extends UserDefinedContextConfigBase {
  type: UserDefinedContextType.Numeric;
  numericDescriptionId: string;
  numericDescription: NumericDescriptionOutputV1;
}

// Union type for all context config types
export type UserDefinedContextConfigType = UserDefinedContextLabelsConfig | UserDefinedContextNumericConfig;

// Type guards for safe type checking
export const isToggleContextConfig = (config: UserDefinedContextConfigType): config is UserDefinedContextLabelsConfig =>
  config.type === UserDefinedContextType.Toggle;

export const isLabelContextConfig = (config: UserDefinedContextConfigType): config is UserDefinedContextLabelsConfig =>
  config.type === UserDefinedContextType.Label;

export const isLabelBasedContextConfig = (
  config: UserDefinedContextConfigType,
): config is UserDefinedContextLabelsConfig => isToggleContextConfig(config) || isLabelContextConfig(config);

export const isNumericContextConfig = (
  config: UserDefinedContextConfigType,
): config is UserDefinedContextNumericConfig => config.type === UserDefinedContextType.Numeric;
